import { CSSProperties, ReactNode } from 'react'
import clsx from 'clsx'
import Image from 'next/image'
import { PortableTextBlock } from 'next-sanity'

import { urlFor } from 'lib/sanity/image'
import CustomPortableText from 'lib/sanity/richText/CustomPortableText'
import { ButtonVariants } from 'shared/ui/Button/Button.enums'
import LinkButton from 'shared/ui/Button/LinkButton'
import FormattedText from 'shared/ui/FormattedText'

import { SectionTitle } from './styles'

import styles from './MediaTextBlock.module.scss'

interface MediaTextBlockProps {
  header: string | ReactNode
  content: PortableTextBlock
  imageSrc: string
  imageAlt: string
  imageSide?: 'left' | 'right'
  mobileImageSide?: 'top' | 'bottom'
  color?: string
  cta?: {
    text: string
    link: string
  }
  srcIconList?: string
  className?: string
  headerSize?: '2xl' | '3xl'
}
const MediaTextBlock = ({
  header,
  content,
  imageSrc,
  imageAlt,
  imageSide = 'left',
  mobileImageSide = 'top',
  color,
  cta,
  srcIconList,
  headerSize = '2xl',
  className
}: MediaTextBlockProps) => {
  return (
    <div
      className={clsx(
        styles.contentWrapper,
        {
          [styles.imageLeft]: imageSide === 'left',
          [styles.mobileImageTop]: mobileImageSide === 'top',
          [styles.mobileImageBottom]: mobileImageSide !== 'top'
        },
        className
      )}
      style={
        color
          ? ({ '--media-text-block-bg': color } as CSSProperties)
          : undefined
      }
    >
      <div className={styles.textWrapper}>
        <SectionTitle $size={headerSize} className={styles.sectionTitle}>
          {typeof header === 'string' ? (
            <FormattedText text={header} />
          ) : (
            header
          )}
        </SectionTitle>
        <div
          className={clsx(styles.description, {
            [styles.descriptionWithIcons]: srcIconList
          })}
          style={
            srcIconList
              ? ({
                  '--description-icon': `url(${srcIconList})`
                } as CSSProperties)
              : undefined
          }
        >
          <CustomPortableText value={content || []} />
        </div>

        {cta && (
          <LinkButton
            className={styles.ctaButton}
            href={cta.link}
            variant={ButtonVariants.primary}
            size='large'
          >
            {cta.text}
          </LinkButton>
        )}
      </div>
      <div
        className={clsx(styles.imageWrapper, {
          [styles.imageWrapperMobileTop]: mobileImageSide === 'top',
          [styles.imageWrapperMobileBottom]: mobileImageSide !== 'top'
        })}
      >
        <Image
          src={urlFor(imageSrc).url()}
          alt={imageAlt}
          width={800}
          height={800}
          quality={90}
          sizes={'(max-width: 764px) 400px, (max-width: 1024px) 300px, 500px'}
        />
      </div>
    </div>
  )
}

export default MediaTextBlock
